import { ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useSubscription } from '@/hooks/useSubscription';
import { useAuth } from '@/hooks/useAuth';
import { Check, Crown, Zap, ArrowRight, Clock } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import useSubscriptionConfig from '@/hooks/useSubscriptionConfig';
import { Switch } from '@/components/ui/switch';
import { annualSavings, annualTotal } from '@/lib/pricing';

interface SubscriptionGuardProps {
  children: ReactNode;
  requiredFeature?: string;
  fallbackComponent?: ReactNode;
}

const SubscriptionGuard = ({
  children,
  requiredFeature,
  fallbackComponent
}: SubscriptionGuardProps) => {
  const { subscribed, subscription_tier, loading, createCheckout, bypass_subscription } = useSubscription();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation('subscription');
  const config = useSubscriptionConfig();
  // Este paywall enseñaba SIEMPRE el precio anual y llamaba a createCheckout sin
  // intervalo, o sea cobrando el mensual: el usuario leía $229 y se le cobraban
  // $289/mes. La cadencia ahora es estado, arranca en mensual como el resto de
  // superficies, y es la misma que viaja a Stripe (projects#369).
  const [annual, setAnnual] = useState(false);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Bypass (family/internal) or active subscription — let them in
  if (bypass_subscription || (subscribed && subscription_tier)) {
    return <>{children}</>;
  }

  // Same contract as FeatureGate: `fallbackComponent={null}` means "render
  // nothing", so test for presence rather than truthiness.
  if (fallbackComponent !== undefined) {
    return <>{fallbackComponent}</>;
  }

  const handlePlanSelect = async (planId: string) => {
    if (!user) {
      navigate('/auth');
      return;
    }

    const plan = config.getPlanById(planId);
    if (!plan) return;

    if (plan.comingSoon) {
      toast({
        title: t('guard.comingSoonToast'),
        description: t('ctaText.developmentToast', { plan: plan.displayName }),
      });
      return;
    }

    try {
      const checkoutUrl = await createCheckout(plan.id, annual ? 'annual' : 'monthly');
      window.location.href = checkoutUrl;
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast({
        title: t('guard.errorTitle'),
        description: t('ctaText.checkoutFailedFallback'),
        variant: 'destructive',
      });
    }
  };

  const pricingPlans = config.plans;

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted/20 p-4">
      <div className="container mx-auto max-w-6xl">
        <div className="text-center mb-12 pt-8">
          <Badge variant="secondary" className="mb-4">
            <Crown className="h-4 w-4 mr-1" />
            {t('guard.badge')}
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
            {t('guard.welcomeTitle')}
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            {t('guard.pickPlan')}
            {requiredFeature && t('guard.featureRequired')}
          </p>
          <p className="text-sm text-muted-foreground mt-2">
            {t('guard.fineprint')}
          </p>
        </div>

        <div className="mb-8 flex items-center justify-center gap-3 text-sm">
          <span className={annual ? 'text-muted-foreground' : 'font-medium text-foreground'}>
            {t('guard.billingMonthly')}
          </span>
          <Switch checked={annual} onCheckedChange={setAnnual} aria-label={t('guard.billingToggleAria')} />
          <span className={annual ? 'font-medium text-foreground' : 'text-muted-foreground'}>
            {t('guard.billingAnnual')}
          </span>
        </div>

        <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto mb-12">
          {pricingPlans.map((plan) => {
            const IconComponent = plan.icon;
            return (
              <Card
                key={plan.name}
                className={`relative transition-all duration-300 hover:scale-105 ${
                  plan.popular
                    ? 'border-primary shadow-xl ring-2 ring-primary/20'
                    : 'border-border hover:border-primary/50'
                }`}
              >
                {plan.popular && (
                  <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-primary">
                    {t('guard.mostPopular')}
                  </Badge>
                )}
                <CardHeader className="text-center pb-2">
                  <div className="flex justify-center mb-4">
                    <div className={`p-3 rounded-full ${plan.popular ? 'bg-primary/10' : 'bg-muted'}`}>
                      <IconComponent className={`h-8 w-8 ${plan.popular ? 'text-primary' : 'text-muted-foreground'}`} />
                    </div>
                  </div>
                  <CardTitle className="text-2xl">{plan.displayName}</CardTitle>
                  <div className="text-4xl font-bold text-primary">
                    ${annual ? plan.price_annual : plan.price}
                    <span className="text-lg text-muted-foreground font-normal">
                      {annual ? t('guard.perMoBilledAnnually') : t('guard.perMo')}
                    </span>
                  </div>
                  {/* El cargo real y el ahorro, en una sola línea y siempre que
                      se ofrezca el anual: `price_annual` es el equivalente
                      mensual, Stripe cobra 12× de una vez (projects#369). */}
                  {annual ? (
                    <p className="text-xs tabular-nums text-muted-foreground">
                      {t('guard.annualChargeAndSavings', {
                        total: annualTotal(plan.price_annual).toLocaleString('en-US'),
                        savings: annualSavings(plan.price, plan.price_annual).toLocaleString('en-US'),
                      })}
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground">{t('guard.monthlyNoCommitment')}</p>
                  )}
                  <CardDescription className="text-base mt-1">{t(plan.descriptionKey)}</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 mb-8">
                    {plan.features.filter(f => f.included).map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center gap-3">
                        {feature.comingSoon ? (
                          <Clock className="h-5 w-5 text-orange-500 flex-shrink-0" />
                        ) : (
                          <Check className="h-5 w-5 text-primary flex-shrink-0" />
                        )}
                        <span className={`text-sm ${feature.comingSoon ? 'text-orange-600' : ''}`}>
                          {t(feature.nameKey)}
                          {feature.comingSoon && <span className="ml-1 text-xs">{t('guard.comingSoonInline')}</span>}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    size="lg"
                    className={`w-full group ${
                      plan.popular
                        ? 'bg-primary hover:bg-primary/90'
                        : plan.comingSoon
                        ? 'bg-orange-500 hover:bg-orange-600'
                        : ''
                    }`}
                    variant={plan.popular || plan.comingSoon ? 'default' : 'outline'}
                    onClick={() => handlePlanSelect(plan.id)}
                  >
                    {t(plan.ctaTextKey)}
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="text-center bg-card rounded-2xl p-8 border">
          <h3 className="text-2xl font-bold mb-4">{t('guard.whyTitle')}</h3>
          <div className="grid md:grid-cols-3 gap-6 max-w-3xl mx-auto">
            <div className="text-center">
              <div className="bg-primary/10 p-3 rounded-full w-16 h-16 mx-auto mb-3 flex items-center justify-center">
                <Zap className="h-8 w-8 text-primary" />
              </div>
              <h4 className="font-semibold mb-2">{t('guard.why.speed')}</h4>
              <p className="text-sm text-muted-foreground">{t('guard.why.speedBody')}</p>
            </div>
            <div className="text-center">
              <div className="bg-primary/10 p-3 rounded-full w-16 h-16 mx-auto mb-3 flex items-center justify-center">
                <Crown className="h-8 w-8 text-primary" />
              </div>
              <h4 className="font-semibold mb-2">{t('guard.why.apps')}</h4>
              <p className="text-sm text-muted-foreground">{t('guard.why.appsBody')}</p>
            </div>
            <div className="text-center">
              <div className="bg-primary/10 p-3 rounded-full w-16 h-16 mx-auto mb-3 flex items-center justify-center">
                <Check className="h-8 w-8 text-primary" />
              </div>
              <h4 className="font-semibold mb-2">{t('guard.why.noContracts')}</h4>
              <p className="text-sm text-muted-foreground">{t('guard.why.noContractsBody')}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionGuard;
